import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  useClearinghouseStatsQuery,
  useInfiniteBorrowersQuery,
} from "~/types/graphql";
import { useV2ProtocolData, useV2Accounts } from "~/hooks/useV2Data";
import { useV1UtilizationData } from "~/hooks/useV1UtilizationData";
import { Link } from "react-router";
import { ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import { ProtocolUtilizationChart } from "~/components/ProtocolUtilizationChart";

export default function Overview() {
  const { data: v1Data, isLoading: v1Loading } = useClearinghouseStatsQuery({});
  const { data: v1UtilizationData, isLoading: v1UtilizationLoading } =
    useV1UtilizationData();
  const { data: v1BorrowersData, isLoading: v1BorrowersLoading } =
    useInfiniteBorrowersQuery(
      {
        first: 1000,
        skip: 0,
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.borrowerStats_collection.length < 1000) {
            return undefined;
          }
          return { skip: allPages.length * 1000 };
        },
        initialPageParam: { skip: 0 },
      }
    );
  const { data: v2Data, isLoading: v2Loading } = useV2ProtocolData();
  const { data: v2AccountsData, isLoading: v2AccountsLoading } =
    useV2Accounts();

  const isLoading =
    v1Loading ||
    v1UtilizationLoading ||
    v1BorrowersLoading ||
    v2Loading ||
    v2AccountsLoading;

  if (isLoading) return <div>Loading...</div>;

  // Get the latest V1 data from utilization snapshots for DEBT
  const latestV1Data =
    v1UtilizationData && v1UtilizationData.length > 0
      ? v1UtilizationData[v1UtilizationData.length - 1]
      : null;

  // Get all borrowers from all pages for COLLATERAL
  const allBorrowers =
    v1BorrowersData?.pages.flatMap((page) => page.borrowerStats_collection) ??
    [];

  // V1 debt from snapshot stats (matches the chart)
  const v1TotalDebt = latestV1Data?.totalPrincipalReceivables || 0;
  // V1 collateral from individual borrower records
  const v1TotalCollateral = allBorrowers.reduce(
    (acc, curr) => acc + Number(curr.currentCollateral || 0),
    0
  );

  // Debug logging
  console.log("V1 Data Sources:", {
    latestV1Data,
    allBorrowersCount: allBorrowers.length,
    v1TotalCollateral,
    v1TotalDebt,
    debtFromSnapshots: latestV1Data?.totalPrincipalReceivables,
    collateralFromBorrowers: v1TotalCollateral,
  });

  const v1TotalBorrowers =
    v1Data?.clearinghouseCumulativeStats_collection.reduce(
      (acc, curr) => acc + curr.totalUniqueBorrowers,
      0
    ) || 0;

  const v1ActiveLoans =
    v1Data?.clearinghouseCumulativeStats_collection.reduce(
      (acc, curr) => acc + curr.currentActiveLoans,
      0
    ) || 0;

  // V2 data from hooks
  const v2TotalCollateral = v2Data?.totalCollateral || 0;
  const v2TotalDebt = v2Data?.totalDebt || 0;
  const v2TotalAccounts = v2AccountsData?.length || 0;
  const v2ActivePositions =
    v2AccountsData?.filter((account) => account.debt > 0).length || 0;

  // Combined totals
  const totalCollateral = v1TotalCollateral + v2TotalCollateral;
  const totalDebt = v1TotalDebt + v2TotalDebt;
  const totalUsers = v1TotalBorrowers + v2TotalAccounts;
  const totalPositions = v1ActiveLoans + v2ActivePositions;

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatGOHM = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-8">
      {/* Protocol Overview */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Protocol Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Collateral
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatGOHM(totalCollateral)} gOHM
              </div>
              <p className="text-xs text-muted-foreground">
                Across all Versions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Debt</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatUSD(totalDebt)}</div>
              <p className="text-xs text-muted-foreground">Outstanding debt</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Borrowers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalUsers}</div>
              <p className="text-xs text-muted-foreground">Unique Borrowers</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active Positions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalPositions}</div>
              <p className="text-xs text-muted-foreground">Loans & positions</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Version Comparison */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Version Comparison</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cooler v1 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Cooler V1
                </CardTitle>
                <Link
                  to="/cooler-v1"
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  View Details <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Collateral</p>
                  <p className="text-lg font-semibold">
                    {formatGOHM(v1TotalCollateral)} gOHM
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Debt</p>
                  <p className="text-lg font-semibold">
                    {formatUSD(v1TotalDebt)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Borrowers</p>
                  <p className="text-lg font-semibold">{v1TotalBorrowers}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Loans</p>
                  <p className="text-lg font-semibold">{v1ActiveLoans}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cooler v2 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Cooler V2
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded dark:bg-green-900 dark:text-green-300">
                    Current
                  </span>
                </CardTitle>
                <Link
                  to="/cooler-v2"
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  View Details <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Collateral</p>
                  <p className="text-lg font-semibold">
                    {formatGOHM(v2TotalCollateral)} gOHM
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Debt</p>
                  <p className="text-lg font-semibold">
                    {formatUSD(v2TotalDebt)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Borrowers</p>
                  <p className="text-lg font-semibold">{v2TotalAccounts}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Active Positions
                  </p>
                  <p className="text-lg font-semibold">{v2ActivePositions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Protocol Utilization Chart */}
      <div>
        <ProtocolUtilizationChart days={120} />
      </div>
    </div>
  );
}
